import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminQuiz() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    pergunta: '',
    opcoes: ['', '', '', ''],
    resposta_correta: 0,
    dificuldade: 'FACIL',
    categoria: '',
    plan_required: 'FREE',
    pontos: 10
  });

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/quiz/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar questões');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingQuestion) {
        await axios.put(`${BACKEND_URL}/api/admin/quiz/questions/${editingQuestion.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Questão atualizada');
      } else {
        await axios.post(`${BACKEND_URL}/api/quiz/questions`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Questão criada');
      }
      
      setShowDialog(false);
      setEditingQuestion(null);
      resetForm();
      loadQuestions();
    } catch (error) {
      toast.error('Erro ao salvar questão');
    }
  };

  const deleteQuestion = async (questionId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta questão?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/quiz/questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Questão excluída');
      loadQuestions();
    } catch (error) {
      toast.error('Erro ao excluir questão');
    }
  };

  const openEditDialog = (question) => {
    setEditingQuestion(question);
    setFormData({
      pergunta: question.pergunta,
      opcoes: question.opcoes,
      resposta_correta: question.resposta_correta,
      dificuldade: question.dificuldade,
      categoria: question.categoria,
      plan_required: question.plan_required,
      pontos: question.pontos
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      pergunta: '',
      opcoes: ['', '', '', ''],
      resposta_correta: 0,
      dificuldade: 'FACIL',
      categoria: '',
      plan_required: 'FREE',
      pontos: 10
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Gerenciar Questões Quiz
              </h1>
              <p className="text-muted-foreground text-lg">
                Total: {questions.length} questões
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingQuestion(null);
                resetForm();
                setShowDialog(true);
              }}
              className="bg-gradient-to-r from-orange-500 to-red-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Questão
            </Button>
          </div>

          <div className="bg-white rounded-2xl border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-semibold">Pergunta</th>
                    <th className="text-left p-4 font-semibold">Categoria</th>
                    <th className="text-left p-4 font-semibold">Dificuldade</th>
                    <th className="text-left p-4 font-semibold">Pontos</th>
                    <th className="text-left p-4 font-semibold">Plano</th>
                    <th className="text-left p-4 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {questions.map((question) => (
                    <tr key={question.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                      <td className="p-4 max-w-md">
                        <p className="line-clamp-2">{question.pergunta}</p>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                          {question.categoria}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          question.dificuldade === 'FACIL' ? 'bg-green-100 text-green-700' :
                          question.dificuldade === 'MEDIO' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {question.dificuldade}
                        </span>
                      </td>
                      <td className="p-4 font-semibold">{question.pontos}</td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          question.plan_required === 'PREMIUM' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {question.plan_required}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          <Button
                            onClick={() => openEditDialog(question)}
                            size="sm"
                            variant="outline"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => deleteQuestion(question.id)}
                            size="sm"
                            variant="destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingQuestion ? 'Editar Questão' : 'Nova Questão'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Pergunta</label>
              <Input
                value={formData.pergunta}
                onChange={(e) => setFormData({ ...formData, pergunta: e.target.value })}
                placeholder="Digite a pergunta"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Opções de Resposta</label>
              <div className="mb-2">
                <Input
                  value={formData.opcoes[0] || ''}
                  onChange={(e) => {
                    const newOpcoes = [...formData.opcoes];
                    newOpcoes[0] = e.target.value;
                    setFormData({ ...formData, opcoes: newOpcoes });
                  }}
                  placeholder="Opção 1"
                  required
                />
              </div>
              <div className="mb-2">
                <Input
                  value={formData.opcoes[1] || ''}
                  onChange={(e) => {
                    const newOpcoes = [...formData.opcoes];
                    newOpcoes[1] = e.target.value;
                    setFormData({ ...formData, opcoes: newOpcoes });
                  }}
                  placeholder="Opção 2"
                  required
                />
              </div>
              <div className="mb-2">
                <Input
                  value={formData.opcoes[2] || ''}
                  onChange={(e) => {
                    const newOpcoes = [...formData.opcoes];
                    newOpcoes[2] = e.target.value;
                    setFormData({ ...formData, opcoes: newOpcoes });
                  }}
                  placeholder="Opção 3"
                  required
                />
              </div>
              <div className="mb-2">
                <Input
                  value={formData.opcoes[3] || ''}
                  onChange={(e) => {
                    const newOpcoes = [...formData.opcoes];
                    newOpcoes[3] = e.target.value;
                    setFormData({ ...formData, opcoes: newOpcoes });
                  }}
                  placeholder="Opção 4"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Resposta Correta</label>
                <select
                  value={formData.resposta_correta}
                  onChange={(e) => setFormData({ ...formData, resposta_correta: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white"
                >
                  <option value={0}>Opção 1</option>
                  <option value={1}>Opção 2</option>
                  <option value={2}>Opção 3</option>
                  <option value={3}>Opção 4</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Dificuldade</label>
                <select
                  value={formData.dificuldade}
                  onChange={(e) => setFormData({ ...formData, dificuldade: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white"
                >
                  <option value="FACIL">Fácil</option>
                  <option value="MEDIO">Médio</option>
                  <option value="DIFICIL">Difícil</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Categoria</label>
                <Input
                  value={formData.categoria}
                  onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                  placeholder="Ex: História"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Pontos</label>
                <Input
                  type="number"
                  value={formData.pontos}
                  onChange={(e) => setFormData({ ...formData, pontos: parseInt(e.target.value) })}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Plano</label>
                <select
                  value={formData.plan_required}
                  onChange={(e) => setFormData({ ...formData, plan_required: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white"
                >
                  <option value="FREE">Grátis</option>
                  <option value="PREMIUM">Premium</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-gradient-to-r from-orange-500 to-red-500">
                {editingQuestion ? 'Atualizar' : 'Criar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
